import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  Button,
  useColorModeValue,
  Icon,
  HStack,
  Image
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaCode, FaMobile, FaRobot, FaServer, FaLaptopCode, FaPaintBrush, FaBug, FaCloudUploadAlt } from 'react-icons/fa';
import { MdLightbulb, MdSupportAgent } from "react-icons/md";
import Appointment from './Appointment'; // Import the Appointment component

const MotionBox = motion(Box);

// Color variables from environment
const primaryColor = process.env.REACT_APP_PRIMARY_COLOR || "#3182CE";
const secondaryColor = process.env.REACT_APP_SECONDARY_COLOR || "#319795";
const backgroundColor = process.env.REACT_APP_BACKGROUND_COLOR || "#F7FAFC";
const textColor = process.env.REACT_APP_TEXT_COLOR || "#2D3748";
const cardBackground = process.env.REACT_APP_CARD_BACKGROUND || "#FFFFFF";
const gradientStart = process.env.REACT_APP_GRADIENT_START || "blue.500";
const gradientEnd = process.env.REACT_APP_GRADIENT_END || "teal.500";

const services = [
  {
    title: 'Website Development',
    description: 'We create responsive and modern websites tailored to your business needs. Our team ensures each website is optimized for speed, SEO, and user experience, making sure your business stands out online. We also provide maintenance and support services to keep your website running smoothly.',
    icon: FaCode,
    imageUrl: "https://miro.medium.com/v2/resize:fit:1200/0*M4bxiCIjcTK-2Xr6.jpeg",
    features: ['Responsive Design', 'SEO Optimization', 'Custom Development', 'Ongoing Maintenance'],
    color: "blue.500"
  },
  {
    title: 'App Development',
    description: 'Our team builds user-friendly and functional mobile applications for both Android and iOS platforms. We specialize in creating apps that offer seamless performance and an engaging user experience, whether for business, entertainment, or utility purposes.',
    icon: FaMobile,
    imageUrl: 'https://img.freepik.com/free-vector/app-development-banner_33099-1720.jpg',
    features: ['Native & Cross-Platform', 'Intuitive UI/UX', 'Scalable Architecture', 'App Store Deployment'],
    color: "purple.500"
  },
  {
    title: 'API Development',
    description: 'We provide robust API development services to ensure seamless integration with your systems. Our APIs are designed for scalability, security, and ease of use, allowing different software applications to communicate effectively and enhancing the functionality of your systems.',
    icon: FaServer,
    imageUrl: "https://static.vecteezy.com/system/resources/previews/010/376/649/non_2x/api-application-programming-interface-in-the-shape-of-sphere-with-hexagon-pattern-in-wireframe-hand-software-development-tool-information-technology-and-business-concept-illustration-vector.jpg",
    features: ['Secure & Reliable', 'Seamless Integration', 'RESTful & GraphQL APIs', 'Documentation & Support'],
    color: "teal.500"
  },
  {
    title: 'Chatbot & WhatsApp Bot Development',
    description: 'We develop intelligent chatbots and WhatsApp bot solutions to enhance customer engagement, automate support, and provide a convenient and effective way to reach your audience on their preferred messaging platforms.',
    icon: FaRobot,
    imageUrl: "https://www.wati.io/wp-content/uploads/2024/02/25-WhatsApp-Chatbot-For-Business-Build-A-WhatsApp-Bot-In-5-Minutes-%E2%80%93-3.png",
    features: ['AI-Powered Conversations', '24/7 Customer Support', 'Multi-platform Deployment', 'Customizable Flows'],
    color: "green.500"
  },
  {
    title: 'Generative AI Development Services',
    description: 'Harness the power of Generative AI to build innovative applications, automate content creation, and unlock new possibilities for your business.',
    icon: FaLaptopCode,
    imageUrl: "https://www.solulab.com/wp-content/uploads/2024/07/Generative-AI-Development-Company-1.jpg",
    features: ['Content Generation', 'Creative Applications', 'Model Training & Fine-tuning', 'Integration with Existing Systems'],
    color: "orange.500"
  },
  {
    title: 'Developer Outsourcing',
    description: 'Our Software Outsourcing services include:\n- IT Staff Augmentation\n- Dedicated Development Teams\n- Shadow Engineers\n- Offshore Software Development\n- Offshore Development Center\n- White Label Services\nWe provide flexible and scalable solutions to extend your development capabilities.',
    icon: FaLaptopCode,
    imageUrl: "https://images.prismic.io/turing/652ec685fbd9a45bcec81a0a_Software_Development_Outsourcing_58145ee5c8.webp?auto=format,compress",
    features: ['Flexible Team Options', 'Cost-Effective Solutions', 'Access to Global Talent', 'Focus on Your Core Business'],
    color: "pink.500"
  }
]; 

const howWeWorkSteps = [
  {
    title: 'Discovery & Planning',
    description: 'We start by understanding your unique needs and defining the project scope.',
    icon: MdLightbulb,
    imageUrl: "https://ik.imagekit.io/3ys7hawgp/ChatGPT%20Image%20May%2023,%202025,%2007_24_03%20PM.png?updatedAt=1748008509225",
    color: "blue.500"
  },
  {
    title: 'Design & Prototyping',
    description: 'Our design team creates the look, feel, and interactive prototypes.',
    icon: FaPaintBrush,
    imageUrl: "https://ik.imagekit.io/3ys7hawgp/ChatGPT%20Image%20May%2023,%202025,%2007_28_39%20PM.png?updatedAt=1748008864979",
    color: "purple.500"
  },
  {
    title: 'Development',
    description: 'We bring the design to life by building the solution.',
    icon: FaLaptopCode,
    imageUrl: "https://images.unsplash.com/photo-1542831371-29b0f74f9713?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
    color: "teal.500"
  },
  {
    title: 'Testing & QA',
    description: 'Rigorous testing ensures a robust and bug-free solution.',
    icon: FaBug,
    imageUrl: "https://ik.imagekit.io/3ys7hawgp/ChatGPT%20Image%20May%2023,%202025,%2007_31_17%20PM.png?updatedAt=1748008929367",
    color: "green.500"
  },
  {
    title: 'Deployment',
    description: 'We deploy your service smoothly and efficiently.',
    icon: FaCloudUploadAlt,
    imageUrl: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
    color: "orange.500"
  },
  {
    title: 'Support & Maintenance',
    description: 'We provide ongoing support and maintenance to keep your service running perfectly.',
    icon: MdSupportAgent,
    imageUrl: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
    color: "pink.500"
  },
];

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const ServicesOffered = () => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);

  const handleBackToServices = () => {
    setShowAppointmentForm(false);
  };

  return (
    <Box bg={backgroundColor} pt={showAppointmentForm ? "0" : "130px"}>
      {showAppointmentForm ? (
        <Box minH="calc(100vh - 130px - 80px)">
          <Container maxW="container.xl" py={10}>
            <Button onClick={handleBackToServices} mb={6} bg={primaryColor} color="white" _hover={{ bg: secondaryColor }}>Back to Services</Button>
            <Appointment isEmbeddedAsForm={true} />
          </Container>
        </Box>
      ) : (
        <>
          {/* Hero Section */}
          <Box
            backgroundImage="url(https://t3.ftcdn.net/jpg/04/42/44/98/360_F_442449827_ispo2oI83ffX0TSax4Pgdd7xkqCA5ThA.jpg)"
            backgroundSize="cover"
            backgroundPosition="center"
            backgroundAttachment="fixed"
                   top="-14"
          height="60vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="relative"
        >
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="rgba(0,0,0,0.6)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            color="white"
            zIndex="1"
          >
            <Container maxW="container.xl" textAlign="center" position="relative" zIndex="2">
              <VStack spacing={4}>
                <Heading
                  as="h1"
                  size="2xl"
                  mb={4}
                  textAlign="center"
                  bgGradient={`linear(to-r, ${gradientStart}, ${gradientEnd})`}
                  bgClip='text'
                  fontWeight="bold"
                  color='transparent'
                >
                  Our Services
                </Heading>
                <Text fontSize="xl" maxW="800px" textAlign="center" px={4} color="gray.200">
                  We offer a comprehensive range of technology solutions to help your business thrive in the digital age.
                </Text>
              </VStack>
            </Container>
          </Box>
        </Box>

        {/* Services Section */}
        <Container maxW="container.xl" py={20}>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={10}>
          {services.map((service, index) => (
              <MotionBox
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                height="100%"
              >
                <Box
                  bg={cardBg}
                  p={8}
                  borderRadius="lg"
                  boxShadow="lg"
                  height="100%"
                  _hover={{
                    transform: 'translateY(-5px)',
                    boxShadow: 'xl',
                  }}
                  transition="all 0.3s"
                >
                  <VStack spacing={4} align="start">
                    <Image
                      src={service.imageUrl}
                      alt={service.title}
                      borderRadius="md"
                      objectFit="cover"
                      mb={4}
                      width="100%"
                      maxH="200px"
                    />
                    <Icon as={service.icon} w={10} h={10} color={service.color} />
                    <Heading 
                      as="h3" 
                      size="lg"
                      bgGradient={`linear(to-r, ${gradientStart}, ${gradientEnd})`}
                      bgClip='text'
                      fontWeight="bold"
                    >
                      {service.title}
                    </Heading>
                    <Text color="gray.600">
                      {service.description}
                      {service.features && (
                        <VStack align="start" spacing={1} mt={4}>
                          {service.features.map((feature, featIndex) => (
                            <HStack key={featIndex} spacing={2}>
                              <Icon as={FaLaptopCode} w={3} h={3} color={secondaryColor} />
                              <Text fontSize="sm" color="gray.600">{feature}</Text>
                            </HStack>
                          ))}
                        </VStack>
                      )}
                    </Text>
                  </VStack>
                </Box>
              </MotionBox>
            ))}
          </SimpleGrid>

          <MotionBox
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            transition={{ duration: 0.5, delay: 0.2 }}
            textAlign="center"
            mt={12}
          >
            <Button
              size="lg"
              bg={primaryColor}
              color="white"
              _hover={{ bg: secondaryColor }}
              onClick={() => setShowAppointmentForm(true)}
              px={8}
            >
              Book an Appointment
            </Button>
          </MotionBox>

        </Container>

        {/* How We Work Section */}
        <Container maxW="container.xl" py={10}>
          <VStack spacing={10} textAlign="center" mb={10}>
            <Heading 
              as="h2" 
              size="xl"
              bgGradient="linear(to-r, blue.400, purple.500)"
              bgClip='text'
              fontWeight="bold"
            >
              Our Process: How We Work
            </Heading>
            <Text fontSize="lg" maxW="800px" color={textColor}>
              We follow a clear and collaborative process to deliver successful projects.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={10}>
            {howWeWorkSteps.map((step, index) => (
              <MotionBox
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Box
                  bg={cardBg}
                  p={6}
                  borderRadius="lg"
                  boxShadow="md"
                  height="100%"
                  _hover={{
                    transform: 'translateY(-5px)',
                    boxShadow: 'lg',
                  }}
                  transition="all 0.3s"
                >
                  <VStack spacing={4} align="center">
                    <Image
                      src={step.imageUrl}
                      alt={step.title}
                      borderRadius="md"
                      objectFit="cover"
                      mb={4}
                      width="100%"
                      maxH="150px"
                    />
                    <Box
                      p={3}
                      borderRadius="full"
                      bg={`${step.color}.10`}
                      color={step.color}
                    >
                      <Icon as={step.icon} w={10} h={10} fill={step.color} />
                    </Box>
                    <Heading 
                      as="h3" 
                      size="md"
                      bgGradient="linear(to-r, blue.400, purple.500)"
                      bgClip='text'
                      fontWeight="bold"
                    >
                      {step.title}
                    </Heading>
                    <Text color={textColor} textAlign="center">
                      {step.description}
                    </Text>
                  </VStack>
                </Box>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>

        {/* Call to Action Section */}
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          transition={{ duration: 0.5 }}
          bgGradient={`linear(to-r, ${gradientStart}, ${gradientEnd})`}
          py={20}
          textAlign="center"
          color="white"
        >
          <Container maxW="container.xl">
            <VStack spacing={6}>
              <Heading 
                as="h2" 
                size="xl"
                bgGradient="linear(to-r, green.200, purple.200)"
                bgClip='text'
                fontWeight="bold"
              >
                Ready to Transform Your Business?
              </Heading>
              <Text fontSize="lg" maxW="600px">
                Partner with us to build innovative solutions that drive growth and efficiency.
              </Text>
              <Button
                size="lg"
                bg="white"
                color={primaryColor}
                _hover={{ bg: "gray.100" }}
                onClick={() => window.location.href = '/contact'}
              >
                Get Started
              </Button>
            </VStack>
          </Container>
        </MotionBox>
      </>
      )}
    </Box>
  );
};

export default ServicesOffered;









